import React, { useEffect } from 'react';
import { Link, TextButton } from '@amzn/storm-ui';
import { useDispatch } from 'react-redux';
import { useWatch } from 'react-hook-form';
import SellingAccountsTiles from '@features/account-registration/components/SellingAccounts/SellingAccountTiles';
import SellingAccountsTitle from './SellingAccountsTitle';
import useGetSellingAccounts from '../../hooks/useGetSellingAccounts';
import EmptyAndErrorContainer from '@components/EmptyAndErrorContainer';
import Spinner from '@components/Spinner';
import isEmpty from 'lodash/isEmpty';
import useIntl from '@i18n/useIntl';
import {
  SELLING_ACCOUNT_EMPTY_STATE_MESSAGE_TRANSLATION_KEY,
  SELLING_ACCOUNT_EMPTY_STATE_TITLE_TRANSLATION_KEY,
  SELLING_ACCOUNT_ERROR_STATE_MESSAGE_TRANSLATION_KEY,
  SELLING_ACCOUNT_ERROR_STATE_TITLE_TRANSLATION_KEY,
} from './constants';
import { SELECTED_SELLING_ACCOUNT_ID } from '@features/account-settings/constants';
import { setIsNonEndemic } from 'src/shared/state/registration-slice';
import { SellingAccountsWrapper } from './style';

type SellingAccountsProps = {
  pageId: string;
  switchAccountUrl?: string;
};
const SellingAccounts = ({
  pageId,
  switchAccountUrl,
}: SellingAccountsProps) => {
  const { intl } = useIntl();
  const dispatch = useDispatch();

  const {
    sellingAccounts = [],
    countryList = [],
    isLoading,
    isError,
  } = useGetSellingAccounts();

  // A registration is endemic only when the user actually selects a selling account to link,
  // not merely because one is available. The server treats a create request with no linked
  // selling account as non-endemic self-service and requires a solved CAPTCHA
  // (AccountController.isNonEndemicSelfServiceRequest). Keying this off availability suppressed
  // the CAPTCHA for users who have a selling account but choose "I don't want to link", so their
  // submit reached the server with neither a selling account nor a CAPTCHA and was rejected with
  // 400. Mirror the server contract: non-endemic iff nothing is selected to link.
  const selectedSellingAccountId = useWatch({
    name: SELECTED_SELLING_ACCOUNT_ID,
  });

  useEffect(() => {
    dispatch(setIsNonEndemic(isEmpty(selectedSellingAccountId)));
  }, [dispatch, selectedSellingAccountId]);

  if (isError) {
    return (
      <EmptyAndErrorContainer
        title={intl.getMessage(
          SELLING_ACCOUNT_ERROR_STATE_TITLE_TRANSLATION_KEY,
        )}
        message={intl.getMessage(
          SELLING_ACCOUNT_ERROR_STATE_MESSAGE_TRANSLATION_KEY,
        )}
      />
    );
  }

  if (isLoading) {
    return <Spinner isFullScreen={false} />;
  }

  return (
    <SellingAccountsWrapper>
      <SellingAccountsTitle />
      {isEmpty(sellingAccounts) ? (
        <EmptyAndErrorContainer
          title={intl.getMessage(
            SELLING_ACCOUNT_EMPTY_STATE_TITLE_TRANSLATION_KEY,
          )}
          message={
            switchAccountUrl ? (
              <TextButton>
                <Link href={switchAccountUrl}>
                  {intl.getMessage(
                    SELLING_ACCOUNT_EMPTY_STATE_MESSAGE_TRANSLATION_KEY,
                  )}
                </Link>
              </TextButton>
            ) : (
              intl.getMessage(
                SELLING_ACCOUNT_EMPTY_STATE_MESSAGE_TRANSLATION_KEY,
              )
            )
          }
        />
      ) : (
        <SellingAccountsTiles
          pageId={pageId}
          countryList={countryList}
          sellingAccounts={sellingAccounts}
        />
      )}
    </SellingAccountsWrapper>
  );
};

export default SellingAccounts;
